/**
 * Recurring > list of schedules (tracker item 29).
 *
 * Filters (status, customerId, propertyId) and the page live in the query
 * string, like PaymentListPage. Route-gated to staff (office roles and
 * TECHNICIAN); only ADMIN / OFFICE_MANAGER see the create button.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { createAPIClient, IRecurringJobsApi } from '../api';
import * as types from '../api/types';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../utils/apiError';
import { canManageRecurring } from '../utils/permissions';
import { describeRecurrence } from '../utils/recurrence';
import { formatJobDate } from '../utils/jobPresentation';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../components/formStyles';
import {
  RECURRING_NEW_PATH,
  recurringDetailPath,
  recurringNewPath,
  recurringStatusClass,
  recurringStatusLabel,
  scheduleAddress,
} from './recurringUi';
import { Toast } from '../components/Toast';

const PAGE_SIZE = 20;

export function RecurringStatusBadge({ status }: { status: string }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${recurringStatusClass(status)}`}>
      {recurringStatusLabel(status)}
    </span>
  );
}

export function RecurringListPage() {
  const api = useMemo<IRecurringJobsApi>(() => createAPIClient(), []);
  const { user } = useAuth();
  const canManage = canManageRecurring(user);
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get('page')) || 1);
  const status = params.get('status') || '';
  const customerId = params.get('customerId') || '';
  const propertyId = params.get('propertyId') || '';
  const [schedules, setSchedules] = useState<types.RecurringSchedule[]>([]);
  const [pagination, setPagination] = useState<types.PaginationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    api.listRecurringSchedules(page, PAGE_SIZE, {
      status: status as types.RecurringScheduleStatus,
      customerId: customerId || undefined,
      propertyId: propertyId || undefined,
    })
      .then((result) => { if (active) { setSchedules(result.data); setPagination(result.pagination); } })
      .catch((cause) => { if (active) setError(getErrorMessage(cause, 'Could not load recurring schedules')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [api, page, status, customerId, propertyId]);

  function changeParams(next: Record<string, string>) {
    const updated = new URLSearchParams(params);
    Object.entries(next).forEach(([key, value]) => (value ? updated.set(key, value) : updated.delete(key)));
    setParams(updated);
  }

  const pages = Math.max(1, pagination?.pages || 1);
  // Name the customer / property filter from the rows when possible
  const filteredCustomer = customerId ? schedules.find((s) => s.customerId === customerId)?.customer.name ?? customerId : '';
  const filteredProperty = propertyId ? (() => {
    const match = schedules.find((s) => s.propertyId === propertyId);
    return match ? scheduleAddress(match.property) : propertyId;
  })() : '';

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-2">
        <h1 className="text-2xl font-bold text-gray-900">Recurring services</h1>
        {canManage && (
          <Link to={customerId || propertyId ? recurringNewPath({ customerId, propertyId }) : RECURRING_NEW_PATH} className={`${primaryButtonClass} block w-full text-center py-2.5 sm:inline-block sm:w-auto sm:py-2`}>
            New schedule
          </Link>
        )}
      </div>
      <p className="mb-6 max-w-3xl text-sm text-gray-600">
        Each schedule repeats a draft template job at a property. Jobs are created when someone clicks <strong>Generate now</strong> on a
        schedule, unless the server's background generator is turned on (it is off by default).
      </p>

      <section className="bg-white rounded-lg shadow p-5 sm:p-6">
        <div className="flex flex-wrap items-end gap-4 mb-5">
          <div className="w-full sm:max-w-xs">
            <label className={labelClass} htmlFor="recurring-status">Status</label>
            <select id="recurring-status" className={`${inputClass} text-base sm:text-sm`} value={status} onChange={(event) => changeParams({ status: event.target.value, page: '' })}>
              <option value="">All statuses</option>
              {types.RECURRING_SCHEDULE_STATUSES.map((value) => <option key={value} value={value}>{recurringStatusLabel(value)}</option>)}
            </select>
          </div>
          {(customerId || propertyId) && (
            <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700 min-w-0 break-words">
              {customerId && <span>Customer: <strong>{filteredCustomer}</strong></span>}
              {propertyId && <span>Property: <strong>{filteredProperty}</strong></span>}
              <button type="button" className={`${secondaryButtonClass} py-2.5 sm:py-2`} onClick={() => changeParams({ customerId: '', propertyId: '', page: '' })}>
                Clear filter
              </button>
            </div>
          )}
        </div>

        {error && <Toast kind="error">{error}</Toast>}
        {loading ? <p>Loading schedules…</p> : error ? null : (
          <>
            {schedules.length === 0 ? (
              <p className="py-10 text-center text-gray-600">
                No recurring schedules found.
                {canManage && <> <Link to={RECURRING_NEW_PATH} className="text-blue-700 underline">Create one from a draft job</Link>.</>}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="responsive-table min-w-full divide-y divide-gray-200 text-sm">
                  <caption className="sr-only">Recurring schedules</caption>
                  <thead className="bg-gray-50">
                    <tr>
                      {['Customer', 'Property', 'Pattern', 'Next to generate from', 'Last generated', 'Status'].map((heading) => (
                        <th key={heading} scope="col" className="px-4 py-3 text-left font-medium text-gray-600">{heading}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {schedules.map((schedule) => (
                      <tr key={schedule.id}>
                        <td data-label="Customer" data-primary className="px-4 py-3 break-words"><Link to={`/customers/${schedule.customerId}`} className="text-blue-700 hover:underline">{schedule.customer.name}</Link></td>
                        <td data-label="Property" className="px-4 py-3 break-words">{scheduleAddress(schedule.property)}</td>
                        <td data-label="Pattern" className="px-4 py-3">
                          <Link to={recurringDetailPath(schedule.id)} className="font-semibold text-blue-700 hover:underline">
                            {describeRecurrence(schedule.frequency, schedule.interval, schedule.frequencyDetails)}
                          </Link>
                          {schedule.endDate && <span className="block text-xs text-gray-500">until {formatJobDate(schedule.endDate)}</span>}
                        </td>
                        <td data-label="Next to generate from" className="px-4 py-3 whitespace-nowrap">{schedule.status === 'CANCELLED' ? '—' : schedule.nextScheduledDate ? formatJobDate(schedule.nextScheduledDate) : '—'}</td>
                        <td data-label="Last generated" className="px-4 py-3 whitespace-nowrap">{schedule.lastGeneratedDate ? formatJobDate(schedule.lastGeneratedDate) : 'Never'}</td>
                        <td data-label="Status" className="px-4 py-3"><RecurringStatusBadge status={schedule.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {pages > 1 && (
              <div className="flex flex-wrap justify-between items-center gap-3 mt-6">
                <button type="button" className={`${secondaryButtonClass} py-2.5 sm:py-2`} disabled={page <= 1} onClick={() => changeParams({ page: String(page - 1) })}>Previous</button>
                <span className="text-sm text-gray-600">Page {page} of {pages}</span>
                <button type="button" className={`${secondaryButtonClass} py-2.5 sm:py-2`} disabled={page >= pages} onClick={() => changeParams({ page: String(page + 1) })}>Next</button>
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
}
